import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import Text from "../../Text";
import { ROUTE_PATHS } from "../../../../../constants/routeConstants";
import { useLazyFetchSearchQuery } from "../../../../../stores/api/pages.api";

const ALL_SECTIONS = [
  { key: "products", label: "Products", type: "product" },
  { key: "blogs", label: "Blogs", type: "blog" },
];

const TYPE_PLACEHOLDER = {
  product: "Search products…",
  blog: "Search blogs…",
  "": "Search products and blogs…",
};

const SearchModal = ({ onClose, searchType = "" }) => {
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const [trigger, { data: results = { products: [], blogs: [], brands: [] }, isFetching }] = useLazyFetchSearchQuery();

  const sections = searchType
    ? ALL_SECTIONS.filter((s) => s.type === searchType)
    : ALL_SECTIONS;

  // Saare active items ki flat array navigation ke liye
  const flatResults = useMemo(() => {
    if (!query.trim() || isFetching) return [];
    return sections.flatMap(({ key }) => results[key] ?? []);
  }, [sections, results, query, isFetching]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    inputRef.current?.focus();
    
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Keyboard Navigation Listener
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
      } else if (e.key === "Enter") {
        if (activeIndex >= 0 && flatResults[activeIndex]) {
          e.preventDefault();
          const targetItem = flatResults[activeIndex];
          window.location.href = targetItem.href;
          handleClose();
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [flatResults, activeIndex]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (query.trim()) trigger({ q: query, type: searchType });
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query, trigger, searchType]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 250);
  };

  const hasResults = flatResults.length > 0;

  // Global index counter taaki pure layout mein navigation continuous rahe
  let itemCounter = 0;

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex flex-col transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

      {/* Modal panel */}
      <div
        className={`relative z-10 w-full bg-white shadow-2xl transition-transform duration-250 ${visible ? "translate-y-0" : "-translate-y-4"}`}
      >
        {/* Search bar row */}
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center gap-3">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={TYPE_PLACEHOLDER[searchType] ?? TYPE_PLACEHOLDER[""]}
            className="flex-1 text-sm sm:text-base text-gray-900 placeholder-gray-400 outline-none bg-transparent min-w-0"
          />
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0"
            aria-label="Close search"
          >
            <X size={20} />
          </button>
        </div>

        <div className="border-t border-gray-100" />

        {/* Results Container */}
        <div className="max-w-[860px] mx-auto px-4 sm:px-6 py-4 sm:py-6 max-h-[70vh] overflow-x-auto overflow-y-hidden">
          {!query.trim() ? (
            <p className="text-sm text-gray-400">Start typing to search…</p>
          ) : isFetching ? (
            <p className="text-sm text-gray-400">Searching…</p>
          ) : !hasResults ? (
            <p className="text-sm text-gray-400">No results for "{query}"</p>
          ) : (
            <div className="space-y-8">
              {sections.map(({ key, label }) => {
                const items = results[key] ?? [];
                if (items.length === 0) return null;
                
                return (
                  <div key={key} className="flex flex-col">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-3 sticky left-0">
                      {label}
                    </p>
                    
                    {/* 8-by-8 wrapping column container */}
                    <ul 
                      className="flex flex-col flex-wrap gap-x-6 gap-y-1 content-start"
                      style={{ 
                        // Ek item ki height approx 36px hai + 4px gap. 
                        // 8 items ke liye container height exact lock kardi taaki 9th item next column mein jump kare
                        height: "320px" 
                      }}
                    >
                      {items.map((item) => {
                        const currentIndex = itemCounter++;
                        const isActive = currentIndex === activeIndex;

                        return (
                          <li key={item.label} className="w-[240px] sm:w-[260px] list-none">
                            <Text
                              is="a"
                              href={item.href}
                              className={`text-sm leading-snug block px-2 py-1.5 rounded-md transition-all duration-150 truncate ${
                                isActive
                                  ? "bg-gray-100 text-blue-600 font-medium translate-x-1"
                                  : "text-gray-700 hover:text-blue-500 hover:bg-gray-50"
                              }`}
                              onClick={handleClose}
                            >
                              {item.label}
                            </Text>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SearchModal;